/**
 * Passer à l'impôt sur les sociétés : ce que l'option change pour une
 * entreprise individuelle au réel ou une EURL à l'impôt sur le revenu.
 *
 * DEUX QUESTIONS, ET L'OUTIL LES SÉPARE
 *
 *   1. Si vous vous versez tout, l'IS vous fait-il gagner quelque chose ? On
 *      compare l'impôt sur le revenu au meilleur partage entre rémunération et
 *      dividendes à l'IS. Le plus souvent, presque rien : la rémunération d'un
 *      gérant cotise comme le bénéfice d'un indépendant, et les dividendes
 *      cotisent au-delà de 10 %.
 *   2. Si vous ne prenez que ce dont vous avez besoin, l'IS laisse le reste
 *      dans la société après un impôt de 15 % jusqu'à 42 500 € de bénéfice.
 *      C'est là tout son intérêt, et aussi son piège : cet argent n'est pas
 *      encore à vous, et le sortir plus tard coûtera encore l'impôt sur le
 *      revenu et, au-delà du seuil, des cotisations. L'IS ne fait pas payer
 *      moins : il fait payer plus tard, et laisse l'argent travailler entre
 *      deux.
 *
 * LES RÈGLES DE L'OPTION, relevées le 24 septembre 2026 sur impots.gouv.fr
 * (fiche modifiée le 5 mars 2026) et le BOFiP BOI-BIC-CHAMP-70-10 : ouverte à
 * l'entrepreneur individuel au régime réel, par assimilation irrévocable à une
 * EURL ; notifiée avant la fin du troisième mois de l'exercice ; une société
 * peut y renoncer jusqu'au cinquième exercice suivant, après quoi elle est
 * définitive.
 */

import {
    apresFraisProfessionnels,
    ECART_NEGLIGEABLE,
    cotisationsIndependant,
    impotSocietes,
    scenarioReel,
} from "./businessStructure.js";
import { meilleurPartage } from "./salaryDividends.js";
import { impotSurLeRevenu } from "./incomeTax.js";

const positif = (x) => Math.max(0, x);

function impotAjoute(revenuImposable, { autresRevenus = 0, ...foyer }) {
    const avec = impotSurLeRevenu(positif(autresRevenus + revenuImposable), foyer);
    const sans = impotSurLeRevenu(positif(autresRevenus), foyer);
    return positif(avec - sans);
}

/**
 * Ce que touche, après cotisations et impôt, un gérant travailleur
 * indépendant à qui la société consacre `enveloppe` : rémunération et
 * cotisations comprises, sans dividendes.
 */
export function netRemuneration({ enveloppe, activite, artisan, foyer }) {
    const c = cotisationsIndependant({ revenuAvantCotisations: enveloppe, activite, artisan });
    const remuneration = positif(enveloppe - c.total);
    const imposable =
        remuneration > 0 ? apresFraisProfessionnels(remuneration + c.nonDeductible) : 0;
    return { cout: Math.max(enveloppe, c.total), net: remuneration - impotAjoute(imposable, foyer) };
}

export function comparerOptionIs({
    forme = "ei",
    benefice,
    capital = 1000,
    activite = "services",
    artisan = false,
    foyer = {},
    besoin = null,
}) {
    const ir = scenarioReel({ chiffreAffaires: benefice, charges: 0, activite, artisan, foyer });
    const is = meilleurPartage({
        forme: "eurl",
        enveloppe: benefice,
        capital,
        activite,
        artisan,
        foyer,
        seuilSurBenefice: forme === "ei",
    });
    const gain = is.meilleur.net - ir.net;

    // Garder de l'argent dans la société : la rémunération qui donne tout
    // juste le besoin, et ce qui reste à la société après l'impôt.
    let garde = null;
    if (besoin !== null && besoin > 0) {
        const commun = { activite, artisan, foyer };
        const maximum = netRemuneration({ enveloppe: benefice, ...commun }).net;
        if (maximum < besoin) {
            garde = { atteignable: false };
        } else {
            let bas = 0;
            let haut = benefice;
            for (let i = 0; i < 50; i++) {
                const milieu = (bas + haut) / 2;
                if (netRemuneration({ enveloppe: milieu, ...commun }).net >= besoin) haut = milieu;
                else bas = milieu;
            }
            const cout = netRemuneration({ enveloppe: haut, ...commun }).cout;
            const resultat = positif(benefice - cout);
            const impot = impotSocietes(resultat);
            garde = {
                atteignable: true,
                enveloppe: haut,
                resultat,
                impotSocietes: impot,
                reserve: resultat - impot,
                // À l'impôt sur le revenu, ce qui dépasse le besoin est déjà
                // imposé, et dans votre poche.
                epargneIr: positif(ir.net - besoin),
                irSuffit: ir.net >= besoin,
            };
        }
    }

    return {
        ir,
        is,
        gain,
        gagnant: Math.abs(gain) < ECART_NEGLIGEABLE ? null : gain > 0 ? "is" : "ir",
        garde,
    };
}
